import { formatCPF } from '@brazilian-utils/brazilian-utils';
import { Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import moment from 'moment';
import { Children } from "react";
import { useCreatePath } from 'react-admin';
import { useNavigate } from 'react-router-dom';
interface GenericDatagridProps {
    children: any;
    data: object[];
    style: object;
    title: string | undefined;
    sort: { field: string, order: any } | undefined;
    setSort: Function | undefined;
    pagination: { page: number, perPage: number } | undefined;
    setPagination: Function | undefined;
    total: number;
    textAlign: "right" | "inherit" | "left" | "center" | "justify" | "end" | "initial" | "start" | "-moz-initial" | "revert" | "unset" | "match-parent" | undefined;
    rowClick: string | undefined;
};

const GenericDatagrid = ({
    children,
    data,
    style,
    title,
    sort,
    setSort,
    pagination,
    setPagination,
    total,
    textAlign,
    rowClick,
}: GenericDatagridProps) => {
    const navigate = useNavigate();
    const createPath = useCreatePath();

    const handleClick = (rowClick: string | undefined, record: any) => async (event: React.MouseEvent) => {
        const id = record.id;
        if (!rowClick || !id) return;
        event.persist();
        navigate(createPath({ resource: rowClick, id, type: 'show' }));
    };

    return (
        <>
            {title && <Typography sx={{ margin: '20px 0 20px 0', textAlign: 'center' }}>{title}</Typography>}
            <TableContainer sx={{ margin: '0 2%' }}>
                <Table sx={{ width: '96%' }} style={{ ...style }}>
                    <TableHead>
                        <TableRow>
                            {Children.map(children, ({ props: { label, source, unsortable } }) => {
                                let content = label ? label : source;

                                if (!unsortable && sort && setSort) {
                                    const isSortField = sort.field === source;
                                    content = (
                                        <TableSortLabel
                                            active={isSortField}
                                            direction={isSortField ? sort.order.toLowerCase() : 'desc'}
                                            onClick={() => setSort({
                                                field: source,
                                                order: !isSortField || sort.order === 'ASC' ? 'DESC' : 'ASC'
                                            })}
                                        >
                                            {content}
                                        </TableSortLabel>
                                    );
                                }
                                return (
                                    <TableCell style={{ width: `calc(100%/${children.length})`, textAlign }}>
                                        {content}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data && (data.length === 0 ?
                            <TableRow>
                                <TableCell style={{ border: 'none' }}>Nenhum Registro</TableCell>
                            </TableRow> :
                            data.map((record, i) =>
                                <TableRow key={i} onClick={handleClick(rowClick, record)} hover={!!rowClick} sx={{ cursor: rowClick ? 'pointer' : 'default' }} >
                                    {Children.map(children, ({ props: { render, source, emptyText, fallbackSource } }, i) => {
                                        let content = '';
                                        if (render) {
                                            content = render(record);
                                        } else {
                                            if (!record[source]) { source = fallbackSource; }
                                            if (source === 'createdAt' || source === 'created_at') {
                                                content = moment(record[source]).format('DD/MM/YYYY   HH:mm');
                                            } else if (source === 'cpf') {
                                                content = formatCPF(record[source]);
                                            } else {
                                                content = record[source];
                                            }
                                        }
                                        content = content || emptyText || '';
                                        return (
                                            <TableCell key={i} sx={{ padding: 0.75, textAlign }}>
                                                {content}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>))
                        }
                    </TableBody>
                </Table>
                {pagination && setPagination && pagination.page && pagination.perPage &&
                    <Pagination
                        count={total ? total : 0}
                        rowsPerPage={pagination.perPage}
                        defaultPage={(pagination.page - 1)}
                        backIconButtonText='Página Anterior'
                        nextIconButtonText='Próxima Página'
                        labelRowsPerPage='Linhas por Página'
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                        component='div'
                        sx={{ width: '96%' }}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        onPageChange={(event, page) => setPagination({ perPage: pagination.perPage, page: page + 1 })}
                        onChangeRowsPerPage={({ target: { value } }) => setPagination({ perPage: value, page: 1 })}
                    />}
            </TableContainer >
        </>);
};

GenericDatagrid.defaultProps = {
    textAlign: 'left',
    data: [],
    style: {},
    total: 0
}

export default GenericDatagrid;